import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Filter, ChevronLeft, ChevronRight, Clock,
  Shield, UserX, UserCheck, KeyRound, PauseCircle, Eye, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const actionConfig = {
  suspend_account_soft: { label: 'Soft Lock aktiviert', icon: PauseCircle, color: 'text-amber-400' },
  suspend_account_hard: { label: 'Hard Lock aktiviert', icon: PauseCircle, color: 'text-red-400' },
  unsuspend_account_soft: { label: 'Soft Lock entfernt', icon: Shield, color: 'text-emerald-400' },
  unsuspend_account_hard: { label: 'Hard Lock entfernt', icon: Shield, color: 'text-emerald-400' },
  suspend_account: { label: 'Account gesperrt', icon: PauseCircle, color: 'text-red-400' },
  unsuspend_account: { label: 'Account entsperrt', icon: Shield, color: 'text-emerald-400' },
  start_impersonation: { label: 'Support-Session gestartet', icon: Eye, color: 'text-amber-400' },
  end_impersonation: { label: 'Support-Session beendet', icon: XCircle, color: 'text-slate-400' },
  admin_password_reset: { label: 'Passwort-Reset ausgelöst', icon: KeyRound, color: 'text-blue-400' },
  deactivate_user: { label: 'Benutzer deaktiviert', icon: UserX, color: 'text-red-400' },
  reactivate_user: { label: 'Benutzer aktiviert', icon: UserCheck, color: 'text-emerald-400' },
};

export default function AdminAuditLog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  const [actionFilter, setActionFilter] = useState(searchParams.get('action') || '');

  useEffect(() => {
    fetchLogs();
  }, [searchParams]);

  const fetchLogs = async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');  // FIX: access_token statt cashx_token
    
    const params = new URLSearchParams();
    if (searchParams.get('action')) params.set('action', searchParams.get('action'));
    params.set('page', searchParams.get('page') || '1');
    params.set('limit', '30');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/audit-log?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch logs');
      
      const data = await response.json();
      setLogs(data.logs);
      setPagination({
        page: data.page,
        pages: data.pages,
        total: data.total
      });
    } catch (error) {
      toast.error('Fehler beim Laden des Audit-Logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') params.set('action', value);
    else params.delete('action');
    params.set('page', '1');
    setSearchParams(params);
    setActionFilter(value);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="admin-auditlog-title"
        >
          Audit-Log
        </h1>
        <p className="text-slate-400 mt-1">
          {pagination.total} Einträge insgesamt
        </p>
      </div>

      {/* Filter */}
      <Card 
        className="p-4 mb-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex gap-4">
          <Select 
            value={actionFilter || 'all'} 
            onValueChange={handleFilterChange}
          >
            <SelectTrigger 
              className="w-full md:w-64 bg-[#0E1628] border-slate-700"
              data-testid="auditlog-action-filter"
            >
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Aktion filtern" />
            </SelectTrigger>
            <SelectContent className="bg-[#111C32] border-slate-700">
              <SelectItem value="all">Alle Aktionen</SelectItem>
              <SelectItem value="suspend_account">Account gesperrt</SelectItem>
              <SelectItem value="unsuspend_account">Account entsperrt</SelectItem>
              <SelectItem value="start_impersonation">Support-Session gestartet</SelectItem>
              <SelectItem value="end_impersonation">Support-Session beendet</SelectItem>
              <SelectItem value="admin_password_reset">Passwort-Reset</SelectItem>
              <SelectItem value="deactivate_user">Benutzer deaktiviert</SelectItem>
              <SelectItem value="reactivate_user">Benutzer aktiviert</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Log List */}
      <Card style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}>
        {loading ? (
          <div className="p-4 space-y-4">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full bg-slate-700 rounded-lg" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Keine Log-Einträge gefunden</p>
            <p className="text-sm mt-1">Admin-Aktionen werden hier protokolliert</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {logs.map((log) => {
              const config = actionConfig[log.action] || {
                label: log.action,
                icon: FileText,
                color: 'text-slate-400'
              };
              const ActionIcon = config.icon;
              
              return (
                <div
                  key={log.log_id}
                  className="p-4 hover:bg-slate-800/30 transition-colors"
                  data-testid={`log-entry-${log.log_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-slate-800 ${config.color}`}>
                      <ActionIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className={`font-medium ${config.color}`}>{config.label}</p>
                          <p className="text-sm text-slate-400">
                            von <span className="text-white">{log.admin_name}</span>
                            <span className="text-slate-500"> ({log.admin_email})</span>
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-slate-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(log.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      {/* Target Info */}
                      <div className="mt-2 flex items-center gap-2">
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                          {log.target_type}: {log.target_id?.substring(0, 8)}...
                        </Badge>
                        {log.target_type === 'tenant' && (
                          <Link
                            to={`/admin/accounts/${log.target_id}`}
                            className="text-xs text-[#2F80FF] hover:underline"
                          >
                            Account anzeigen →
                          </Link>
                        )}
                      </div>
                      
                      {/* Details */}
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 p-2 rounded bg-slate-800/50 text-xs font-mono">
                          {Object.entries(log.details).map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <span className="text-slate-500">{key}:</span>
                              <span className="text-slate-300">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-800">
            <p className="text-sm text-slate-400">
              Seite {pagination.page} von {pagination.pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
